<template>
  <v-container>
    <v-layout>
      <v-flex xs12>
        <h1 class="display-1 mt-10">Algebra</h1>
        <p>Compute the relation between variants.</p>
        <v-sheet elevation="2" class="mt-10">
          <v-sheet class="pl-10 pt-5 pb-5 pr-10" color="light-blue lighten-5">
            <v-row>
              <strong class="overline">{{ getTitleText() }}</strong>
              <v-spacer></v-spacer>
              <v-menu open-on-hover bottom left content-class="elevation-2">
                <template #activator="{ on, attrs }">
                  <v-btn color="blue" icon v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item class="text-right">
                    <v-btn small text color="primary" @click="switchMode()">{{
                      getSwitchText()
                    }}</v-btn>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-row>
          </v-sheet>
          <v-sheet class="pa-5">
            <v-form ref="form" v-model="valid" :lazy-validation="lazy">
              <v-row v-if="mode == 'sequence'" class="pl-2 pr-2">
                <v-col cols="12">
                  <v-text-field
                    v-model="reference"
                    :rules="rules"
                    :label="'Reference sequence'"
                    :hint="'E.g. ATTAAA'"
                    :clearable="true"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row v-if="mode == 'sequence'" class="pl-2 pr-2">
                <v-col cols="12" sm="9" lg="9">
                  <v-text-field
                    v-model="lhs"
                    :rules="rules"
                    :label="'LHS'"
                    :hint="getLhsHintText(lhs_type)"
                    :clearable="true"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="3" lg="3">
                  <v-select
                    v-model="lhs_type"
                    :rules="rules"
                    :items="['sequence', 'variant']"
                    :clearable="true"
                    label="Type"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row v-if="mode == 'hgvs'" class="pl-2 pr-2">
                <v-col cols="12">
                  <v-text-field
                    v-model="lhs"
                    :rules="rules"
                    :label="'LHS'"
                    :hint="getLhsHintText(lhs_type)"
                    :clearable="true"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row v-if="mode == 'sequence'" class="pl-2 pr-2">
                <v-col cols="12" sm="9" lg="9">
                  <v-text-field
                    v-model="rhs"
                    :rules="rules"
                    :label="'RHS'"
                    :hint="getLhsHintText(rhs_type)"
                    :clearable="true"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="3" lg="3">
                  <v-select
                    v-model="rhs_type"
                    :rules="rules"
                    :items="['sequence', 'variant']"
                    :clearable="true"
                    label="Type"
                  ></v-select>
                </v-col>
              </v-row>

              <v-row v-if="mode == 'hgvs'" class="pl-2 pr-2">
                <v-col cols="12">
                  <v-text-field
                    v-model="rhs"
                    :rules="rules"
                    :label="'RHS'"
                    :hint="getLhsHintText(rhs_type)"
                    :clearable="true"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-row class="pl-5 pr-5">
                <span class="example-link" @click="setExample()">Example</span>
              </v-row>
            </v-form>
            <v-row class="pl-5 pr-5 mt-5 mb-5">
              <v-btn
                ref="convert"
                class="mt-5"
                color="primary"
                :disabled="!valid"
                :to="{
                  name: 'Algebra',
                  query: getParams(),
                }"
              >
                Compare
              </v-btn>
            </v-row>
          </v-sheet>
        </v-sheet>

        <v-overlay :absolute="true" :value="loadingOverlay">
          <div class="text-center">
            <v-progress-circular :size="50" indeterminate></v-progress-circular>
          </div>
          <div class="text-center">
            <v-btn class="mt-5" @click="loadingOverlay = false"> Cancel </v-btn>
          </div>
        </v-overlay>

        <v-alert
          v-if="relation"
          ref="successAlert"
          class="mt-10 mb-0"
          elevation="2"
          prominent
          tile
          :color="'green'"
          type="success"
        >
          <Description :description="relation" />
        </v-alert>

        <v-alert
          v-if="response && response.errors"
          prominent
          type="error"
          tile
          elevation="2"
          class="mt-10 mb-0"
        >
          <v-row align="center">
            <v-col class="grow overline"
              >Comparison could not be performed</v-col
            >
          </v-row>
        </v-alert>

        <v-sheet
          v-if="errorsEncountered()"
          class="pt-10 pr-10 pb-8 pl-10 mb-10"
          elevation="2"
          color="red lighten-5"
        >
          <div v-for="(errors, key) in response.errors" :key="key">
            <div class="overline">{{ key }}</div>
            <v-alert
              v-for="(error, index) in errors"
              :key="index"
              color="red lighten-1"
              tile
              border="left"
              dark
            >
              <div>
                {{ getMessage(error) }}
              </div>
            </v-alert>
          </div>
        </v-sheet>

        <v-expansion-panels
          v-if="
            response &&
            (response.ref_seq ||
              response.lhs_seq ||
              response.rhs_seq ||
              response.supremal_lhs ||
              response.supremal_rhs ||
              response.view_lhs_supremal ||
              response.view_rhs_supremal)
          "
          focusable
          hover
          class="mt-5 mb-10"
          tile
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="overline"
              >Details</v-expansion-panel-header
            >
            <v-expansion-panel-content class="pt-5">
              <div v-if="response.ref_seq">
                <div class="overline">Reference Sequence</div>
                <div class="sequence">
                  {{ response.ref_seq }}
                </div>
              </div>
              <div v-if="response.lhs_seq">
                <div class="overline">LHS Sequence</div>
                <div class="sequence">
                  {{ response.lhs_seq }}
                </div>
              </div>
              <div v-if="response.lhs_seq">
                <div class="overline">RHS Sequence</div>
                <div class="sequence">
                  {{ response.rhs_seq }}
                </div>
              </div>
              <div v-if="response.supremal_lhs">
                <div class="overline">LHS Supremal Representation</div>
                <Description
                  :description="response.supremal_lhs.hgvs"
                  :css_class="'ok-description-link'"
                  :to_name="'Normalizer'"
                  :to_params="{ descriptionRouter: response.supremal_lhs.hgvs }"
                  :to_query="getParams()"
                />
                <Description
                  :description="response.supremal_lhs.spdi"
                  :css_class="'ok-description'"
                />
              </div>
              <div v-if="response.supremal_rhs">
                <div class="overline">RHS Supremal Representation</div>
                <Description
                  :description="response.supremal_rhs.hgvs"
                  :css_class="'ok-description-link'"
                  :to_name="'Normalizer'"
                  :to_params="{ descriptionRouter: response.supremal_rhs.hgvs }"
                  :to_query="getParams()"
                />
                <Description
                  :description="response.supremal_rhs.spdi"
                  :css_class="'ok-description'"
                />
              </div>
              <div v-if="response.view_lhs_supremal" class="mt-5">
                <div class="overline">LHS Supremal Variant Overview</div>
                <ViewVariantsCore
                  :view="response.view_lhs_supremal"
                  :d_id="'lhs_supremal'"
                />
              </div>
              <div v-if="response.view_rhs_supremal" class="mt-5">
                <div class="overline">RHS Supremal Variant Overview</div>
                <ViewVariantsCore
                  :view="response.view_rhs_supremal"
                  :d_id="'rhs_supremal'"
                />
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert
          v-if="connectionErrors"
          prominent
          type="error"
          tile
          elevation="2"
          class="mt-10"
          icon="mdi-network-off-outline"
          color="grey darken-4"
        >
          <v-row align="center">
            <v-col class="grow">
              {{ connectionErrors.details }}
            </v-col>
          </v-row>
        </v-alert>

        <v-expansion-panels v-if="response" focusable hover class="mt-10 mb-10">
          <v-expansion-panel>
            <v-expansion-panel-header>Raw Response</v-expansion-panel-header>
            <v-expansion-panel-content>
              <JsonPretty :summary="response" />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import MutalyzerService from "../services/MutalyzerService.js";
import Description from "../components/Description.vue";
import ViewVariantsCore from "../components/ViewVariantsCore.vue";
import JsonPretty from "../components/JsonPretty.vue";

export default {
  components: {
    Description,
    ViewVariantsCore,
    JsonPretty,
  },
  data: () => ({
    valid: true,
    lazy: false,

    mode: "hgvs",

    loadingOverlay: false,

    reference: null,
    reference_type: null,
    lhs: null,
    lhs_type: null,
    lhs_hint: null,
    rhs: null,
    rhs_type: null,
    rhs_hint: null,

    rules: [(value) => !!value || "Required."],

    relation: null,

    response: null,
    connectionErrors: null,
  }),
  watch: {
    $route() {
      this.run();
    },
  },
  created: function () {
    this.run();
  },
  methods: {
    run: function () {
      this.setRouterParams();
      this.compare();
    },
    setRouterParams: function () {
      if (
        this._equals(this.$route.query.reference_type, "sequence") &&
        (this._equals(this.$route.query.lhs_type, "sequence") ||
          this._equals(this.$route.query.lhs_type, "variant")) &&
        (this._equals(this.$route.query.rhs_type, "sequence") ||
          this._equals(this.$route.query.rhs_type, "variant"))
      ) {
        this.mode = "sequence";
        this.reference = this.$route.query.reference;
        this.reference_type = this.$route.query.reference_type;
        this.lhs = this.$route.query.lhs;
        this.lhs_type = this.$route.query.lhs_type;
        this.rhs = this.$route.query.rhs;
        this.rhs_type = this.$route.query.rhs_type;
      } else if (
        !this.$route.query.reference_type &&
        this._equals(this.$route.query.lhs_type, "hgvs") &&
        this._equals(this.$route.query.rhs_type, "hgvs")
      ) {
        this.mode = "hgvs";
        this.lhs = this.$route.query.lhs;
        this.lhs_type = this.$route.query.lhs_type;
        this.rhs = this.$route.query.rhs;
        this.rhs_type = this.$route.query.rhs_type;
      } else if (
        !this.$route.query.reference &&
        !this.$route.query.reference_type &&
        !this.$route.query.lhs &&
        !this.$route.query.lhs_type &&
        !this.$route.query.rhs &&
        !this.$route.query.rhs_type
      ) {
        this.mode = "hgvs";
        this.reset();
      } else {
        this.mode = "hgvs";
        this.reset();
        this.$router.push({
          name: "Algebra",
        });
      }
    },
    _equals: function (p, e) {
      if (p && 0 !== p.length && p == e) {
        return true;
      } else {
        return false;
      }
    },
    reset: function () {
      this.reference = null;
      this.reference_type = null;
      this.lhs = null;
      this.lhs_type = "hgvs";
      this.rhs = null;
      this.rhs_type = "hgvs";
      this.relation = null;
      this.response = null;
    },
    compare: function () {
      if (this.lhs && this.lhs_type && this.rhs && this.rhs_type) {
        this.loadingOverlay = true;
        this.response = null;
        this.relation = null;
        this.connectionErrors = null;
        MutalyzerService.compare(this.getParams())
          .then((response) => {
            this.loadingOverlay = false;
            if (response.data) {
              this.response = response.data;
              if (response.data.relation) {
                this.relation = response.data.relation;
              }
            }
          })
          .catch((error) => {
            this.loadingOverlay = false;
            if (error.response) {
              if (
                error.response.status == 422 &&
                error.response.data &&
                error.response.data.custom
              ) {
                this.response = error.response.data.custom;
              } else {
                this.connectionErrors = {
                  details: "Some response error occured.",
                };
              }
            } else if (error.request) {
              this.connectionErrors = {
                details: "Some connection or server error occured.",
              };
            } else {
              this.connectionErrors = { details: "Some error occured." };
            }
          });
      }
    },
    switchMode: function () {
      if (this.mode == "sequence") {
        this.mode = "hgvs";
        this.reset();
        if (
          !(
            !this.$route.query.reference &&
            !this.$route.query.reference_type &&
            !this.$route.query.lhs &&
            !this.$route.query.lhs_type &&
            !this.$route.query.rhs &&
            !this.$route.query.rhs_type
          )
        ) {
          this.$router.push({
            name: "Algebra",
          });
        }
      } else if (this.mode == "hgvs") {
        this.mode = "sequence";
        this.reset();
        this.reference_type = "sequence";
        this.lhs_type = "variant";
        this.rhs_type = "variant";
      }
      this.relation = null;
    },
    getSwitchText: function () {
      if (this.mode == "hgvs") {
        return "Switch to sequence mode";
      } else if (this.mode == "sequence") {
        return "Switch to HGVS mode";
      }
    },
    getTitleText: function () {
      if (this.mode == "hgvs") {
        return "HGVS Mode";
      } else if (this.mode == "sequence") {
        return "Sequence Mode";
      }
    },
    getLhsHintText: function (hs_type) {
      if (this.mode == "hgvs") {
        return "E.g. NG_012337.3:g.274del";
      } else if (this.mode == "sequence") {
        if (hs_type == null || hs_type == "variant") {
          return "E.g. 2_3del";
        } else if (hs_type == "sequence") {
          return "E.g. ATTGAAT";
        }
      }
    },
    setExample: function () {
      if (this.mode == "hgvs") {
        this.lhs = "NG_012337.3:g.274T>A";
        this.lhs_type = "hgvs";
        this.rhs = "NG_012337.3:g.274del";
        this.rhs_type = "hgvs";
      } else if (this.mode == "sequence") {
        this.reference = "AAAAA";
        this.reference_type = "sequence";
        if (this.lhs_type == "sequence") {
          this.lhs = "ATAAAAA";
        } else {
          this.lhs = "1_2insTA";
        }
        if (this.rhs_type == "sequence") {
          this.rhs = "AATAAA";
        } else {
          this.rhs = "2_3insT";
        }
      }
    },
    getParams: function () {
      if (this.lhs && this.lhs_type && this.rhs && this.rhs_type) {
        if (this.reference && this.reference_type) {
          return {
            reference: this.reference,
            reference_type: this.reference_type,
            lhs: this.lhs,
            lhs_type: this.lhs_type,
            rhs: this.rhs,
            rhs_type: this.rhs_type,
          };
        }
        return {
          lhs: this.lhs,
          lhs_type: this.lhs_type,
          rhs: this.rhs,
          rhs_type: this.rhs_type,
        };
      }
    },
    errorsEncountered: function () {
      if (this.response && this.response.errors) {
        return true;
      } else {
        return false;
      }
    },
    getMessage: function (message) {
      if (message.details) {
        return message.details + " (" + message.code + ")";
      }
      return message;
    },
  },
};
</script>

<style scoped src="../assets/main.css"></style>
<style lang="scss" scoped>
.sequence {
  margin-left: 5px;
  padding: 10px;
  text-decoration: none;
  font-family: monospace;
  display: block;
  color: #000000;
  background-color: #efefef;
  overflow-wrap: break-word;
}
</style>
